<script lang="ts">
  import type {
    GameraTeamReference,
    GoalEvent,
    HitEvent,
    PenaltyEvent,
    ShotMissedEvent,
    ShotSavedEvent,
  } from '@statmuse/core/gamera'
  import { plays, periodClock } from './stores'

  export let awayTeam: GameraTeamReference | undefined
  export let homeTeam: GameraTeamReference | undefined
  export let goalEvents: GoalEvent[] | undefined = undefined
  export let shotEvents: (ShotMissedEvent | ShotSavedEvent)[] | undefined =
    undefined
  export let penaltyEvents: PenaltyEvent[] | undefined = undefined
  export let hitEvents: HitEvent[] | undefined = undefined
  export let period: string | undefined = undefined

  const mapXCoordinate = (x: number) => x * (487 / 2400)
  const mapYCoordinate = (y: number) => -1 * y * (221.1 / 1020) + 221.1

  $: currentPlays = $plays.filter((p) => p.period === $periodClock.period)

  $: goals = goalEvents ?? currentPlays.filter((p) => p.type === 'goal')
  $: shots =
    shotEvents ??
    currentPlays.filter(
      (p) => p.type === 'shotSaved' || p.type === 'shotMissed',
    )

  $: penalties =
    penaltyEvents ?? currentPlays.filter((p) => p.type === 'penalty')
  $: hits = hitEvents ?? currentPlays.filter((p) => p.type === 'hit')
</script>

<svg
  {...$$restProps}
  viewBox="0 0 487 221.1"
  version="1.1"
  xmlns="http://www.w3.org/2000/svg"
  xmlns:xlink="http://www.w3.org/1999/xlink"
>
  <g stroke="#909396" stroke-miterlimit="10">
    <path
      class="dark:fill-gray-3 fill-gray-8"
      d="m49.1.5h388.8c26.8 0 48.6 21.8 48.6 48.6v122.9c0 26.8-21.8 48.6-48.6 48.6h-392.8c-24.6 0-44.6-20-44.6-44.6v-126.9c0-26.8 21.8-48.6 48.6-48.6z"
    />
    <path d="m175.6.5v220.1" />
    <path d="m311.4.5v220.1" />
    <path d="m23.6 215v-206.6" />
    <path d="m.5 75 23.1 9.3" />
    <path d="m.5 146.1 23.1-9.3" />
    <path
      class="dark:fill-gray-3 fill-gray-8"
      d="m23.6 100h9.2c3.7 0 6.7 3 6.7 6.7v7.7c0 3.7-3 6.7-6.7 6.7h-9.2v-21z"
    />
    <path
      class="dark:fill-gray-3 fill-gray-8"
      d="m16.3 101.7h4.9c1.3 0 2.4 1.1 2.4 2.4v12.9c0 1.3-1.1 2.4-2.4 2.4h-4.9v-17.6z"
      transform="matrix(-1 0 0 -1 39.9 221.1)"
    />
    <path d="m464.4 212.6v-204.6" />
    <path d="m487.5 146.1-23.1-9.3" />
    <path d="m487.5 75-23.1 9.3" />
    <path
      class="dark:fill-gray-3 fill-gray-8"
      d="m448.6 100h9.2c3.7 0 6.7 3 6.7 6.7v7.7c0 3.7-3 6.7-6.7 6.7h-9.2v-21z"
      transform="matrix(-1 0 0 -1 913 221.1)"
    />
    <path
      class="dark:fill-gray-3 fill-gray-8"
      d="m464.4 101.7h4.9c1.3 0 2.4 1.1 2.4 2.4v12.9c0 1.3-1.1 2.4-2.4 2.4h-4.9v-17.6z"
    />
    <path d={`m243.5.5v${period ? '184.1' : '221.1'}`} />
    <circle class="dark:fill-gray-3 fill-gray-8" cx="243.5" cy="110.5" r="39" />
    <circle class="dark:fill-gray-3 fill-gray-8" cx="75.9" cy="53.5" r="39" />
    <circle class="dark:fill-gray-3 fill-gray-8" cx="75.9" cy="167.6" r="39" />
    <circle class="dark:fill-gray-3 fill-gray-8" cx="411.1" cy="53.5" r="39" />
    <circle class="dark:fill-gray-3 fill-gray-8" cx="411.1" cy="167.6" r="39" />
  </g>

  {#if period}
    <text font-size="18" fill="#909396" text-anchor="middle" x={243.5} y={206}>
      {period}
    </text>
  {/if}

  <!-- Shots -->
  {#each shots as shot (shot.eventSequence)}
    {#if shot.coordinates}
      {@const team =
        shot.teamId === awayTeam?.teamId
          ? awayTeam
          : shot.teamId === homeTeam?.teamId
          ? homeTeam
          : undefined}

      {#if shot.blockedPlayerId}
        <g fill="none" stroke={team?.colors?.backgroundColor}>
          <circle
            cx={mapXCoordinate(shot.coordinates.x)}
            cy={mapYCoordinate(shot.coordinates.y)}
            r="5.0"
          />
          <path
            d={`m${mapXCoordinate(shot.coordinates.x) + 3.5} ${
              mapYCoordinate(shot.coordinates.y) - 3.5
            }-7.1 7.1`}
            stroke-miterlimit="10"
          />
        </g>
      {:else}
        <circle
          cx={mapXCoordinate(shot.coordinates.x)}
          cy={mapYCoordinate(shot.coordinates.y)}
          r="5"
          stroke={team?.colors?.backgroundColor}
          fill="none"
        />
      {/if}
    {/if}
  {/each}

  <!-- Hits -->
  {#each hits as hit (hit.eventSequence)}
    {#if hit.coordinates}
      {@const team =
        hit.teamId === awayTeam?.teamId
          ? awayTeam
          : hit.teamId === homeTeam?.teamId
          ? homeTeam
          : undefined}
      <path
        stroke={team?.colors?.backgroundColor}
        fill="none"
        d={`m${mapXCoordinate(hit.coordinates.x)} ${
          mapYCoordinate(hit.coordinates.y) - 6
        } 1.1 4.2 4-1.8-2.7 3.5 3.9 2-4.3.1.8 4.3-2.8-3.4-2.8 3.4.9-4.3-4.4-.1 3.9-2-2.6-3.5 4 1.8z`}
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    {/if}
  {/each}

  <!-- Penalties -->
  {#each penalties as penalty (penalty.eventSequence)}
    {#if penalty.coordinates}
      {@const team =
        penalty.teamId === awayTeam?.teamId
          ? awayTeam
          : penalty.teamId === homeTeam?.teamId
          ? homeTeam
          : undefined}
      <g fill="none" stroke={team?.colors?.backgroundColor}>
        <circle
          cx={mapXCoordinate(penalty.coordinates.x) - 3}
          cy={mapYCoordinate(penalty.coordinates.y) - 0.7}
          r="1.1"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d={`m${mapXCoordinate(penalty.coordinates.x) - 3} ${
            mapYCoordinate(penalty.coordinates.y) - 4.5
          }c-2.1 0-3.9 1.7-3.9 3.9s1.7 3.9 3.9 3.9 3.9-1.7 3.9-3.9l5.6-1.4v-2.4h-3.7v1.1h-1.9v-1.1h-3.8z`}
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </g>
    {/if}
  {/each}

  <!-- Goals -->
  {#each goals as goal (goal.eventSequence)}
    {#if goal.coordinates}
      {@const team =
        goal.teamId === awayTeam?.teamId
          ? awayTeam
          : goal.teamId === homeTeam?.teamId
          ? homeTeam
          : undefined}
      <circle
        cx={mapXCoordinate(goal.coordinates.x)}
        cy={mapYCoordinate(goal.coordinates.y)}
        r="5.6"
        fill={team?.colors?.backgroundColor}
      />
    {/if}
  {/each}
</svg>
